import React from 'react';

import { bool, number, object, oneOfType, string } from 'prop-types';

import ControlWrapper from 'components/ControlWrapper';
import InfoIcon from 'theme/components/Icon/InfoIcon';
import { IconButtonComponent } from 'theme/components/IconButton';
import { PopoverComponent } from 'theme/components/Popover';
import { TextComponent } from 'theme/components/Text';
import { FinalFormFieldPropTypes } from 'utils/form/propTypes';
import withFormField from 'utils/form/withFormField';
import { useTranslations } from 'utils/hooks/useTranslations';

import StyledTextArea from './StyledTextArea';
import TextAreaWrapper from './TextAreaWrapper';

const TextArea = ({
  caption,
  captionTooltip,
  optional,
  tooltip,
  visited,
  submitFailed,
  characterLimit,
  ...restProps
}) => {
  const t = useTranslations();

  // `characterLimit` only drives the counter below the field — it is deliberately not forwarded
  // as a `maxlength` attribute, so the user can type past the limit and see the validation error
  // instead of being silently truncated by the browser.
  const enteredCharacters = String(restProps.value ?? '').length;
  const isOverCharacterLimit = !!characterLimit && enteredCharacters > characterLimit;

  return (
    <ControlWrapper
      caption={caption}
      tooltip={captionTooltip}
      optional={optional}
      visited={visited}
      submitFailed={submitFailed}
      errorMessage={restProps.errorMessage}
      // Shares the footer row with the error message, so the two stay aligned.
      footerRight={
        characterLimit ? (
          <TextComponent
            textStyle="sizeXs"
            whiteSpace="nowrap"
            // Matches the top margin ErrorText carries, so both footer items share a baseline
            // whether or not an error is currently shown.
            mt="xxs"
            color={isOverCharacterLimit ? 'status.error' : 'text.tertiary'}
          >
            {t('other.characterCount', { entered: enteredCharacters, limit: characterLimit })}
          </TextComponent>
        ) : null
      }
    >
      <TextAreaWrapper>
        <StyledTextArea data-testid={restProps.name} {...restProps} data-name={restProps.name} />
        {tooltip && (
          <PopoverComponent
            position="absolute"
            zIndex="above"
            top="0"
            right="0"
            transform="translate(-50%, -50%)"
            text={tooltip}
            trigger={
              <IconButtonComponent
                icon={<InfoIcon boxSize={6} display="block" />}
                label={t('buttons.moreInfos')}
              />
            }
          />
        )}
      </TextAreaWrapper>
    </ControlWrapper>
  );
};

TextArea.propTypes = {
  caption: string,
  optional: bool,
  tooltip: string,
  captionTooltip: string,
  visited: bool,
  submitFailed: bool,
  errorMessage: oneOfType([object, string]),
  characterLimit: number,
};

TextArea.defaultProps = {
  caption: null,
  optional: false,
  visited: false,
  submitFailed: false,
  tooltip: null,
  captionTooltip: null,
  errorMessage: '',
  characterLimit: undefined,
};

const TextAreaAdapter = ({ input, meta, ...rest }) => (
  <TextArea {...input} {...meta} {...rest} onChange={(ev) => rest.onChange(ev.target.value)} />
);

TextAreaAdapter.propTypes = FinalFormFieldPropTypes;

export const TextAreaWithField = withFormField(TextAreaAdapter);

export default TextArea;
